import React, { useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";


function OutletMoveTool() {
  const [outletMovesFile, setOutletMovesFile] = useState(null);
  const [mapMasterFile, setMapMasterFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const toolInfo = {
    toolName: "Outlet Move",
    description:
      "Upload the outlet moves spreadsheet and MAP Price Master to build the outlet move workbook.",
    steps: [
      "Upload the Outlet Moves Spreadsheet with SKU, Title, Price, and OUTLET PRICE columns.",
      "Upload the MAP Price Master workbook exported from Google Sheets as .xlsx.",
      "Click 'Outlet Move' to download the enriched workbook.",
    ],
    templates: [],
  };

  const guideTips = [
    "The outlet moves file should stay at one row per SKU. Extra unused columns are okay.",
    "Use the MAP Price Master .xlsx export so the backend can read every vendor tab and resolve Brand plus On MAP status.",
    "SKUs that are not present in the master should land on a separate Needs Master Entry sheet after the backend refactor is deployed.",
  ];

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!outletMovesFile) {
      addToast("Please upload the Outlet Moves spreadsheet.", "error");
      return;
    }

    if (!mapMasterFile) {
      addToast("Please upload the MAP Price Master workbook.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (outletMovesFile.size > maxSize || mapMasterFile.size > maxSize) {
      addToast("One or more files are too large. Maximum size is 100MB.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("outlet_moves_file", outletMovesFile);
    formData.append("map_master_file", mapMasterFile);

    setLoading(true);
    setUploadProgress(0);

    try {
      const response = await flaskApi.post("/variant/outlet-move", formData, {
        responseType: "blob",
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (
        response.data.type === "application/json" ||
        (response.headers["content-type"] &&
          response.headers["content-type"].includes("application/json"))
      ) {
        const text = await response.data.text();
        const json = JSON.parse(text);
        addToast(`Error: ${json.error}`, "error");
        return;
      }

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      const filename = extractedFilename || "Outlet_Move.xlsx";

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      addToast("File downloaded successfully.", "success");
    } catch (error) {
      console.error(error);
      const errorMessage = await extractErrorMessage(error, "Error processing outlet move.");
      addToast(errorMessage, "error");
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <>
      <ToolPageLayout
        title="Outlet Move"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form tool-form-split">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Outlet Moves Spreadsheet</h3>
              </div>
              <ToolFileUploadField
                label="Outlet Moves Spreadsheet"
                file={outletMovesFile}
                setFile={setOutletMovesFile}
                accept=".xlsx,.xls,.csv"
                helperText="Excel or CSV with SKU, Title, Price, and OUTLET PRICE columns."
              />
            </section>

            <div className="tool-action-stack">
              <button type="submit" className="tool-btn-primary" disabled={loading}>
                {loading ? "Processing..." : "Outlet Move"}
              </button>

              <ToolProgressPanel
                loading={loading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading outlet moves and MAP master..."
                processingMessage="Processing outlet move workbook..."
              />
            </div>
          </div>

          <div className="tool-form-side">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">MAP Price Master</h3>
              </div>
              <ToolFileUploadField
                label="MAP Price Master"
                file={mapMasterFile}
                setFile={setMapMasterFile}
                accept=".xlsx"
                helperText="Upload the MAP Price Master exported from Google Sheets as .xlsx."
              />
            </section>
          </div>
        </form>
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default OutletMoveTool;
